import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client.js";
import type { SourceFrameCandidateSummary } from "../api/types.js";

/**
 * Gate 1 Manual Source Frame Picker (2026-09-16): "Choose frame
 * manually" — a large preview of the currently-highlighted candidate,
 * a horizontal filmstrip of every deterministically-qualified frame
 * (never a technically-failed one — the backend already filtered those
 * out before persisting the snapshot), and a single "Use this frame"
 * action. This is a FRAME picker, not a clip editor: the user picks
 * exactly one still frame — there is no trim/range/segment concept
 * anywhere here, deliberately.
 *
 * Nothing is selected when the picker opens (2026-09-22): no thumbnail is
 * highlighted, no large preview is shown, and "Use this frame" stays disabled
 * until the user explicitly clicks a thumbnail.
 *
 * Clicking a thumbnail only changes which one is highlighted — it never
 * calls any provider or makes any paid request. Only "Use this frame"
 * does that: it hands the choice — the candidate's own `sha256`
 * verbatim, so a stale picker (e.g. a second tab open since before a
 * snapshot was replaced) fails clearly instead of silently selecting the
 * wrong frame — to its caller (`onSelect`), which sends
 * `POST .../source-frame-candidates/select`, and closes at once. The
 * request itself is NOT awaited here (2026-09-19): it runs all of Gate 1
 * (~3-4 minutes), so the progress card owns it.
 */
export function ManualSourceFramePicker({
  tenantId,
  artifactId,
  onClose,
  onSelect,
}: {
  readonly tenantId: string;
  readonly artifactId: string;
  readonly onClose: () => void;
  /**
   * Called with the chosen frame's `frameIndex` and its `sha256` verbatim
   * (see this component's top doc comment), the instant the user clicks "Use
   * this frame". The picker closes right after — it does NOT wait for the
   * request: the selection runs the whole of Gate 1 server-side (~3-4
   * minutes), and the CALLER (the progress card) owns that request, shows
   * the processing state, and re-reads durable state when it ends.
   */
  readonly onSelect: (frame: { readonly frameIndex: number; readonly sha256: string }) => void;
}) {
  const [candidates, setCandidates] = useState<readonly SourceFrameCandidateSummary[] | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ candidates: readonly SourceFrameCandidateSummary[] }>(`/videos/${tenantId}/persona-source-stills/${artifactId}/source-frame-candidates`)
      .then((result) => {
        if (cancelled) return;
        // No default selection (2026-09-22): nothing is highlighted, previewed or submittable until the user
        // explicitly clicks a thumbnail — "Use this frame" can never send a frame the user did not pick.
        setCandidates(result.candidates);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error instanceof ApiError ? error.message : "Failed to load candidate frames.");
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, artifactId]);

  const selected = candidates?.find((c) => c.frameIndex === selectedIndex) ?? null;

  function useThisFrame(): void {
    if (selected === null) return;
    onSelect({ frameIndex: selected.frameIndex, sha256: selected.sha256 });
    onClose();
  }

  function formatTimestamp(ms: number): string {
    const totalSeconds = Math.round(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Choose frame manually">
      <div className="modal-content card" style={{ maxWidth: 720 }}>
        <div className="progress-header">
          <span className="progress-stage-label">Choose frame manually</span>
          <button onClick={onClose}>Close</button>
        </div>
        <p className="muted">Pick one source frame. Your choice will not be replaced automatically. Before continuing, the selected frame is checked only to confirm that it contains one clear subject.</p>
        {loadError && <div className="error-banner">{loadError}</div>}

        {candidates === null ? (
          <p className="muted">Loading candidate frames…</p>
        ) : candidates.length === 0 ? (
          <p className="muted">No candidate frames are currently available for this source video.</p>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              {selected === null && (
                <p className="muted" data-testid={`source-frame-nothing-selected-${artifactId}`}>
                  No frame is selected yet — click a frame below to preview it, then use "Use this frame".
                </p>
              )}
              {selected && (
                <img
                  className="preview-image"
                  style={{ width: "100%", maxHeight: 360, objectFit: "contain", background: "#000" }}
                  src={`/videos/${tenantId}/persona-source-stills/${artifactId}/source-frame-candidates/${selected.frameIndex}/preview`}
                  alt={`Frame at ${formatTimestamp(selected.timestampMs)}`}
                  data-testid={`source-frame-candidate-large-preview-${artifactId}`}
                />
              )}
            </div>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8 }}>
              {candidates.map((candidate) => (
                <button
                  key={candidate.frameIndex}
                  onClick={() => setSelectedIndex(candidate.frameIndex)}
                  aria-pressed={candidate.frameIndex === selectedIndex}
                  data-testid={`source-frame-candidate-thumb-${artifactId}-${candidate.frameIndex}`}
                  style={{
                    flex: "0 0 auto",
                    padding: 4,
                    border: candidate.frameIndex === selectedIndex ? "2px solid var(--accent)" : "2px solid transparent",
                    background: "none",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={`/videos/${tenantId}/persona-source-stills/${artifactId}/source-frame-candidates/${candidate.frameIndex}/preview`}
                    alt={`Frame at ${formatTimestamp(candidate.timestampMs)}`}
                    style={{ width: 90, height: 90, objectFit: "cover", display: "block" }}
                  />
                  <div className="muted" style={{ fontSize: 12, textAlign: "center", marginTop: 2 }}>
                    {formatTimestamp(candidate.timestampMs)}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        <div style={{ marginTop: 12 }}>
          <button className="primary" disabled={selected === null} onClick={useThisFrame}>
            Use this frame
          </button>
        </div>
      </div>
    </div>
  );
}
